import { ComponentProps, FC } from "react";

import { useTranslate } from "@/hooks/useTranslate";
import { ChildrenOrI18nType } from "@/utils/react";
import { cn } from "@/utils/tailwind";

import { textVariants } from "./RmText/variants";

export const RmCard: FC<ComponentProps<"div">> = ({ className, ...props }) => {
  return (
    <div
      data-slot="card"
      className={cn(
        "flex flex-col gap-6 rounded-xl border bg-card py-6 text-card-foreground shadow-sm",
        className,
      )}
      {...props}
    />
  );
};

export const RmCardHeader: FC<ComponentProps<"div">> = ({
  className,
  ...props
}) => {
  return (
    <div
      data-slot="card-header"
      className={cn(
        "@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 px-6 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6",
        className,
      )}
      {...props}
    />
  );
};

type RmCardTitlePropsType = ComponentProps<"div"> & ChildrenOrI18nType;

export const RmCardTitle: FC<RmCardTitlePropsType> = ({
  className,
  children,
  i18nKey,
  i18nOptions,
  ...props
}) => {
  const i18nText = useTranslate(i18nKey, i18nOptions);

  return (
    <div
      data-slot="card-title"
      className={cn(
        "text-card-foreground",
        textVariants({
          variant: "lg",
          weight: "bold",
        }),
        className,
      )}
      {...props}>
      {children ?? i18nText}
    </div>
  );
};

type RmCardDescriptionPropsType = ComponentProps<"div"> & ChildrenOrI18nType;

export const RmCardDescription: FC<RmCardDescriptionPropsType> = ({
  className,
  children,
  i18nKey,
  i18nOptions,
  ...props
}) => {
  const i18nText = useTranslate(i18nKey, i18nOptions);

  return (
    <div
      data-slot="card-description"
      className={cn("text-muted-foreground", textVariants(), className)}
      {...props}>
      {children ?? i18nText}
    </div>
  );
};

export const RmCardContent: FC<ComponentProps<"div">> = ({
  className,
  ...props
}) => {
  return (
    <div
      data-slot="card-content"
      className={cn("px-6", className)}
      {...props}
    />
  );
};

export const RmCardFooter: FC<ComponentProps<"div">> = ({
  className,
  ...props
}) => {
  return (
    <div
      data-slot="card-footer"
      className={cn("flex items-center px-6 [.border-t]:pt-6", className)}
      {...props}
    />
  );
};
